'use client'

import {
  IconChevronRight,
  IconDocumentPaperGray,
  IconPersonFemale01Color,
  IconPersonFemale02Color,
  IconPersonMale01Color,
  IconPersonMale02Color,
  IconPhoto,
} from '@macarongfactory/web-icons'
import { Divider, ListRow, OutlineButton, Text } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { Fragment, useState } from 'react'

import { useSeoOnelinkUrl } from '@/shared/external/onelink'

import {
  MODOOCAR_CODE,
  MODOOCAR_REVIEW_APP_ONELINK_URL,
  REVIEW_LABEL_MAX_LENGTH,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type { IMaintenanceReview, TReviewAvatar } from '../_features/modoocar'

const AVATAR_ICON: Record<TReviewAvatar, typeof IconPersonMale01Color> = {
  'male-1': IconPersonMale01Color,
  'male-2': IconPersonMale02Color,
  'female-1': IconPersonFemale01Color,
  'female-2': IconPersonFemale02Color,
}

interface IMaintenanceReviewListProps {
  reviews: IMaintenanceReview[]
  total: number
  moreHref?: string
  costHref?: string
  showAppCta?: boolean
}

const VISIBLE_REVIEW_COUNT = 3
const PROFILE_ICON_SIZE_PX = 40
const EMPTY_ICON_SIZE_PX = 56
const PHOTO_FALLBACK_ICON_SIZE_PX = 32
const CHEVRON_SIZE_PX = 20
const FOOTER_MIN_HEIGHT = 'min-h-[58px]'

const REVIEW_MORE_BUTTON_TEXT = '전체보기'
const EMPTY_TITLE = '아직 정비 리뷰가 없어요'
const EMPTY_COST_BUTTON_TEXT = '정비 비용 보러가기'

const APP_CTA_TEXT = '마이클 앱에서\n전체 정비 리뷰를 볼 수 있어요'
const APP_CTA_BUTTON_TEXT = '앱에서 전체보기'

interface IReviewRowProps {
  review: IMaintenanceReview
  rank: number
  withImpression?: boolean
}

const ReviewRow = ({
  review,
  rank,
  withImpression = true,
}: IReviewRowProps) => {
  const Avatar = AVATAR_ICON[review.avatar ?? 'male-1']
  const [photoError, setPhotoError] = useState(false)
  const showPhoto = Boolean(review.authorPhotoUrl) && !photoError
  const { itemShow } = useModoocarTracking()
  const rowRef = useModoocarImpression<HTMLElement>(() =>
    itemShow(
      MODOOCAR_CODE.SERVICE_REVIEW,
      'review',
      `마이클 정비 후기 | ${review.content.slice(0, REVIEW_LABEL_MAX_LENGTH)}`,
      { rank }
    )
  )
  return (
    <article
      ref={withImpression ? rowRef : undefined}
      className='flex flex-col pb-dimension-x5'
    >
      <ListRow
        left={
          <div className='flex h-dimension-x10 w-dimension-x10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background-surface-basement'>
            {showPhoto ? (
              <img
                // [라이브러리]: React는 하이드레이션 전에 실패한 이미지의 onError를 호출하지 않아서, 마운트할 때 이미 깨진 이미지도 아바타로 바꿉니다.
                ref={img => {
                  if (img && img.complete && img.naturalWidth === 0) {
                    setPhotoError(true)
                  }
                }}
                src={review.authorPhotoUrl}
                alt=''
                className='h-full w-full object-cover'
                onError={() => setPhotoError(true)}
              />
            ) : (
              <Avatar
                width={PROFILE_ICON_SIZE_PX}
                height={PROFILE_ICON_SIZE_PX}
              />
            )}
          </div>
        }
        title={
          <ListRow.Title.Text
            text={review.authorCar}
            typography='title-2-semibold'
            color='text-foreground-neutral-strong'
          />
        }
        lower={
          <Text variant='body-2-medium' color='text-foreground-neutral-normal'>
            {review.date}
          </Text>
        }
      />

      <div className='px-dimension-x5'>
        <Text
          variant='title-2-medium'
          color='text-foreground-neutral-strong'
          className='whitespace-pre-line break-keep'
        >
          {review.content}
        </Text>
      </div>

      {review.photos && review.photos.length > 0 ? (
        <ReviewPhotoGallery
          photos={review.photos}
          alt={`${review.authorCar} 정비 리뷰 사진`}
        />
      ) : null}

      {review.tags && review.tags.length > 0 ? (
        <div className='flex flex-wrap gap-dimension-x2 px-dimension-x5 pt-dimension-x3'>
          {review.tags.map(tag => (
            <KeywordChip key={tag}>{tag}</KeywordChip>
          ))}
        </div>
      ) : null}
    </article>
  )
}

interface IReviewPhotoGalleryProps {
  photos: string[]
  alt: string
}

const ReviewPhotoGallery = ({ photos, alt }: IReviewPhotoGalleryProps) => {
  return (
    <div className='overflow-x-auto pt-dimension-x3'>
      <div className='flex gap-dimension-x2 px-dimension-x5'>
        {photos.map((src, index) => (
          <ReviewPhoto key={`${src}-${index}`} src={src} alt={alt} />
        ))}
      </div>
    </div>
  )
}

interface IReviewPhotoProps {
  src: string
  alt: string
}

const ReviewPhoto = ({ src, alt }: IReviewPhotoProps) => {
  const [hasError, setHasError] = useState(false)

  return (
    <div className='relative size-[100px] shrink-0 overflow-hidden rounded-dimension-x3 bg-background-surface-basement'>
      {hasError ? (
        <div className='absolute inset-0 flex items-center justify-center'>
          <IconPhoto
            width={PHOTO_FALLBACK_ICON_SIZE_PX}
            height={PHOTO_FALLBACK_ICON_SIZE_PX}
            className='text-foreground-neutral-disabled'
          />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className='size-full object-cover'
          onError={() => setHasError(true)}
        />
      )}
    </div>
  )
}

const KeywordChip = ({ children }: { children: string }) => (
  <span className='inline-flex h-[32px] items-center justify-center rounded-full border border-stroke-neutral-strong bg-background-surface-default px-dimension-x3 text-body-2-semibold text-foreground-neutral-normal'>
    {children}
  </span>
)

interface IReviewMoreLinkProps {
  href: string
}

// [주의]: 내부 경로는 <Link>를 써야 basePath(/v2)가 붙습니다. <a>로 바꾸면 /v2가 빠져 404가 납니다. 앱 딥링크(macarongapp://)만 <a>를 씁니다.
const ReviewMoreLink = ({ href }: IReviewMoreLinkProps) => {
  const isInternalPath = href.startsWith('/')
  const { itemClick } = useModoocarTracking()
  const handleClick = () =>
    itemClick(MODOOCAR_CODE.SERVICE_REVIEW, 'button', '정비 후기 전체보기')
  const content = (
    <>
      <Text
        variant='title-1-medium'
        color='text-foreground-neutral-alternative'
      >
        {REVIEW_MORE_BUTTON_TEXT}
      </Text>
      <IconChevronRight
        width={CHEVRON_SIZE_PX}
        height={CHEVRON_SIZE_PX}
        className='text-foreground-neutral-alternative'
      />
    </>
  )
  const linkClassName =
    'inline-flex items-center gap-dimension-x0-5 rounded-dimension-x2 px-dimension-x1-5 py-dimension-x1'

  return (
    <div className='flex flex-col px-dimension-x5'>
      <Divider.Horizontal />
      <div
        className={`flex items-center justify-center py-dimension-x3 ${FOOTER_MIN_HEIGHT}`}
      >
        {isInternalPath ? (
          <Link href={href} onClick={handleClick} className={linkClassName}>
            {content}
          </Link>
        ) : (
          <a href={href} onClick={handleClick} className={linkClassName}>
            {content}
          </a>
        )}
      </div>
    </div>
  )
}

const EmptyResult = ({ costHref }: { costHref?: string }) => (
  <div className='flex flex-col items-center gap-dimension-x3 px-dimension-x5 py-dimension-x5'>
    <IconDocumentPaperGray
      width={EMPTY_ICON_SIZE_PX}
      height={EMPTY_ICON_SIZE_PX}
    />
    <Text variant='title-1-semibold' color='text-foreground-neutral-strong'>
      {EMPTY_TITLE}
    </Text>
    {costHref ? (
      <Link
        href={costHref}
        className='inline-flex items-center rounded-dimension-x2-5 bg-background-neutral-weak px-dimension-x4 py-[8.5px]'
      >
        <Text variant='title-2-semibold' color='text-foreground-neutral-normal'>
          {EMPTY_COST_BUTTON_TEXT}
        </Text>
      </Link>
    ) : null}
  </div>
)

const MaintenanceReviewList = ({
  reviews,
  total,
  moreHref,
  costHref,
  showAppCta = false,
}: IMaintenanceReviewListProps) => {
  const appOnelinkUrl = useSeoOnelinkUrl(MODOOCAR_REVIEW_APP_ONELINK_URL)

  if (reviews.length === 0) return <EmptyResult costHref={costHref} />

  const hasMore = total > VISIBLE_REVIEW_COUNT
  const showAppCtaBlock = showAppCta && reviews.length >= VISIBLE_REVIEW_COUNT
  const visibleReviews = showAppCtaBlock
    ? reviews.slice(0, VISIBLE_REVIEW_COUNT + 1)
    : reviews.slice(0, VISIBLE_REVIEW_COUNT)
  const lastIndex = visibleReviews.length - 1

  const handleAppCtaClick = () => {
    window.open(appOnelinkUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className='flex flex-col'>
      <div className='flex flex-col'>
        {visibleReviews.map((review, index) => {
          const isLast = showAppCtaBlock && index === lastIndex
          const impressionRank = index + 1
          return (
            <Fragment key={review.id}>
              {index > 0 ? (
                <div className='px-dimension-x5'>
                  <Divider.Horizontal />
                </div>
              ) : null}
              {isLast ? (
                <div className='relative'>
                  <div className='absolute inset-0 overflow-clip'>
                    <ReviewRow
                      review={review}
                      rank={impressionRank}
                      withImpression={false}
                    />
                  </div>
                  <div className='relative z-10 flex flex-col items-center gap-dimension-x3 bg-[rgba(255,255,255,0.8)] py-[52px] pl-[65px] pr-[64px] backdrop-blur-[4px]'>
                    <p className='whitespace-pre-line text-center text-[16px] font-semibold leading-[24px] text-foreground-neutral-strong'>
                      {APP_CTA_TEXT}
                    </p>
                    <OutlineButton
                      text={APP_CTA_BUTTON_TEXT}
                      size='medium'
                      variant='brand'
                      clickable={handleAppCtaClick}
                    />
                  </div>
                </div>
              ) : (
                <ReviewRow review={review} rank={impressionRank} />
              )}
            </Fragment>
          )
        })}
      </div>

      {!showAppCtaBlock && hasMore && moreHref ? (
        <ReviewMoreLink href={moreHref} />
      ) : null}
    </div>
  )
}

export default MaintenanceReviewList
